import { Injectable } from '@angular/core';

@Injectable()
export class UrlFormatter {
    /**
     * Byte-identical port of the AngularJS `urlFormatter.format`
     * (account-v1/src/core/urlFormatter/urlFormatter.js). Replaces every `:name`
     * token in `url` with the URI-encoded param value and, when `appendSearch`
     * is true, appends any params NOT present as a `:token` to the query string.
     * Ported verbatim — incl. the `gi` (global/case-insensitive) token replace,
     * the lodash `toPairs` iteration order (via `Object.entries`), the
     * primitive-only search-append guard, and the v1 `dateSerializer`
     * (`YYYY-M-D`, no zero-pad) Date handling — so native callers produce URLs
     * identical to the frozen v1 baseline. (Previously an empty stub.)
     */
    static format(url: string, params?: any, appendSearch?: boolean): string {
        const pairs: [string, any][] = params ? Object.entries(params) : [];
        let search = '';

        const urlHasKey = (key: string) => url.indexOf(':' + key) !== -1;
        const valueCanBeAppendedToSearch = (value: any) =>
            typeof value === 'number' ||
            typeof value === 'string' ||
            value instanceof Date ||
            typeof value === 'boolean';
        const serializeIfNecessary = (value: any) =>
            value instanceof Date
                ? value.getFullYear() + '-' + (value.getMonth() + 1) + '-' + value.getDate()
                : value;

        pairs.forEach(([name, value]) => {
            const appendParamToSearch = !!appendSearch && !urlHasKey(name) && valueCanBeAppendedToSearch(value);
            search += appendParamToSearch
                ? `${name}=${encodeURIComponent(serializeIfNecessary(value))}&`
                : '';
            url = url.replace(new RegExp(':' + name, 'gi'), encodeURIComponent(value));
        });

        if (appendSearch && search.length > 1) {
            url += url.indexOf('?') !== -1
                ? '&' + search.substring(0, search.length - 1)
                : '?' + search.substring(0, search.length - 1);
        }

        return url;
    }

    /**
     * Byte-identical port of v1 `urlFormatter.combineUrlPath` — joins each path
     * segment onto `url` with a single `/` separator (collapsing a duplicate
     * slash at the join). Used by the native short-url list to build the
     * themed-invite base URL.
     */
    static combineUrlPath(url: string, ...paths: string[]): string {
        return paths.reduce(
            (result, part) =>
                (result.slice(-1) === '/' ? result : result + '/') +
                (part.slice(0, 1) === '/' ? part.slice(1) : part),
            url
        );
    }

    static appendStringToQuery(url: string, query: string) {
        if (!url) return null;
        if (!query) return url;
        return url + (url.indexOf('?') > -1 ? '&' : '?') + query;
    }

    static appendObjectToQuery(url: string, query: object) {
        const strQuery = Object.keys(query)
            .filter(key => query.hasOwnProperty(key) && typeof query[key] !== "undefined" && query[key] !== null)
            .map(key => `${key}=${encodeURIComponent(query[key])}`)
            .reduce((prev, curr) => `${prev}${prev.length === 0 ? '' : '&'}${curr}`, "");
        return UrlFormatter.appendStringToQuery(url, strQuery);
    }

    static updateUrlQueryParams(url: string, addParams?: Record<string, string>, removeParams?: string[]): string {
        if (!url || (!removeParams.length && !addParams)) return url;
        const urlObj = new URL(url);
        Object.keys(addParams || {}).forEach(param => urlObj.searchParams.append(param, addParams[param]));
        (removeParams || []).forEach(param => urlObj.searchParams.delete(param));
        return decodeURI(urlObj.toString());
    }
}